import { useMemo } from "react";
import { offlineDb, useLiveQuery } from "@stockline/web/offline";
import type { Email } from "@stockline/shared/types/communications/email";

/**
 * Minimal projection of the offline `emails` table row the recap renders.
 * Derived from the canonical `Email` type via `Pick` so a rename of any of
 * these fields fails to compile here, instead of silently returning
 * `undefined` and degrading every email row to its placeholder values.
 */
export type OfflineEmailSummary = Pick<
  Email,
  "id" | "fromAddress" | "emailSubject" | "emailDate"
>;

const OFFLINE_EMAIL_TABLE = "emails";

/**
 * Resolves rich email summaries (sender / subject / date) for a set of email
 * ids from the offline `emails` Dexie table.
 *
 * Lives here rather than inline in a component because raw `offlineDb` access
 * is a data-access concern, not presentation. The `@stockline/web` `reactive`
 * adapter only exposes equality (`useQuery`) and single-id (`useOne`) lookups
 * — no id-set query — so an `anyOf` against `offlineDb` is the accepted
 * pattern (same as `usePreOrders/offlineShim.ts`). Kept recap-scoped under
 * `recap/hooks/` alongside the other offline-adapter hooks
 * (`useRecapStocksOffline`, `useRecapLocationsOffline`).
 *
 * Key-scoped `anyOf` lookup — an indexed point-get per id, never a full-table
 * scan — so it stays cheap even on tenants with tens of thousands of synced
 * emails (unlike WhatsApp/SMS chats, the emails table is unbounded). Reactive
 * via `useLiveQuery`, so a row whose body syncs in after the first call still
 * enriches. Intended to be called from a component mounted on demand (e.g.
 * the LinkedSourcesPopover body, which Radix unmounts on close) so the
 * subscription is scoped to when the data is actually shown.
 */
export function useEmailSummaries(
  emailIds: string[],
): Map<string, OfflineEmailSummary> {
  // Order-independent, stable dependency key so useLiveQuery only re-subscribes
  // when the actual id set changes, not on every parent re-render.
  const idsKey = useMemo(
    () => [...emailIds].sort((a, b) => a.localeCompare(b)).join(","),
    [emailIds],
  );

  const rows = useLiveQuery(
    async () => {
      if (emailIds.length === 0) return [] as OfflineEmailSummary[];
      return offlineDb
        .table<OfflineEmailSummary>(OFFLINE_EMAIL_TABLE)
        .where("id")
        .anyOf(emailIds)
        .toArray();
    },
    [idsKey],
    [] as OfflineEmailSummary[],
  );

  return useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);
}
